import {debounce} from 'lodash';
import React, {ReactPortal, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

import {Button, FlexColumn, FlexRow, Icon, Text} from '@forecast-it/design-system';
import {ClickAwayListener} from '@material-ui/core';

import {TRACKING_OBJECTS} from '../../../../tracking/amplitude/constants/TrackingObjects';
import {useTrackModal} from '../../../../tracking/amplitude/hooks/useTrackModal';
import {trackEvent} from '../../../../tracking/amplitude/TrackingV2';
import {trackGlobalSearch} from '../../../../tracking/tracking_types/TrackGlobalSearch';
import DeprecatedScrollToNativeScroll from '../../../shared/components/scroll-bars/DeprecatedScrollToNativeScroll';
import useEventListener from '../../../shared/hooks/useEventListener';
import useRoveFocus from '../../../shared/hooks/useRoveFocus';
import {useWindowSize} from '../../../shared/hooks/useWindowSize';
import {parseLocalStorageJSONOrDefault} from '../../../shared/util/LocalStorageUtil';
import GlobalSearchInput from './GlobalSearchInput';
import {MiniLoader} from './MiniLoader';
import {searchQuery, SearchQueryResponse, searchSuggestionQuery} from './SearchQuery';
import SearchResultEmptyState from './SearchResultEmptyState';
import SearchResults from './SearchResults';
import {SearchResultVariant} from './types/SearchResultVariant';

import type {SearchSuggestion} from './types/SearchSuggestion';

export const SEARCH_SHOWN_LIMIT = 5;
export const recentVisitedTaskKey = 'recently-visited-task';

export enum SEARCH_ENTITIES {
	TASK = 'task',
	PROJECT = 'project',
	INTERNAL_TIME = 'internal time',
}

function getStaticContentSize(showGiveFeedback: boolean, showSearchFilters: boolean) {
	const baseStaticContentSize = 168;
	const giveFeedbackSize = showGiveFeedback ? 40 : 0;
	const searchFiltersSize = showSearchFilters ? 100 : 0;
	return baseStaticContentSize + giveFeedbackSize + searchFiltersSize;
}

const SearchModalBackdrop = styled.div`
	height: 100%;
	width: 100%;
	position: absolute;
	top: 0;
	left: 0;
	z-index: 900;
	background: rgba(0, 0, 0, 0.4);
	display: flex;
	justify-content: center;
	align-items: flex-start;
`;

type SearchModalProps = {
	modalYOffset: number;
	modalYPosition?: number;
	modalXPosition?: number;
	modalWidth?: number;
};
const SearchModal = styled.div.attrs(({modalYOffset, modalYPosition, modalXPosition, modalWidth = 800}: SearchModalProps) => ({
	style:
		modalYPosition && modalXPosition
			? {
					position: 'absolute',
					top: `${modalYPosition}px`,
					left: `${modalXPosition}px`,
					width: `${modalWidth}px`,
			  }
			: {
					marginTop: `${modalYOffset}px`,
					width: `${modalWidth}px`,
			  },
}))<SearchModalProps>`
	display: flex;
	flex-direction: column;
	background-color: white;
	border: 1px solid #d3d3df;
	border-radius: 8px;
	padding: 24px;
	gap: 16px;
`;

const SearchInputIconWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: -10px; // small hack to make the clear button look better
`;

const SearchSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	flex: 1;
	min-height: 0;
	max-height: 100%;
`;

const SectionDivider = styled.div`
	flex: 1;
	border-top: 1px solid #d3d3df;
`;

const ShowMore = styled.button`
	padding: 4px 8px;
	cursor: pointer;
	border-radius: 4px;
	color: #008bfa;
	align-self: flex-start;
	border: 2px solid transparent;

	&:hover {
		background-color: #e7e7f3;
	}

	&:focus-visible {
		background-color: #e7e7f3;
		border: 2px solid #3b94ff;
		outline: none;
	}
`;

type GlobalSearchModalConfig = {
	inputPlaceholder: string;
	searchEndpoint: string;
	trackingObject: string;
	recentSearchKey: string;
	allowLinks: boolean;
	showGiveFeedback: boolean;
	allowedEntities: Array<SEARCH_ENTITIES>;
	suggestionsFilter: (searchSuggestion: SearchSuggestion) => boolean;
	baseSearch?: string;
	projectId?: number;
};
const defaultSearchModalConfig: GlobalSearchModalConfig = {
	inputPlaceholder: 'Search for projects and tasks...',
	searchEndpoint: 'globalsearch',
	trackingObject: TRACKING_OBJECTS.GLOBAL_SEARCH_MODAL,
	recentSearchKey: 'recently-searched',
	allowLinks: true,
	showGiveFeedback: true,
	allowedEntities: [SEARCH_ENTITIES.TASK, SEARCH_ENTITIES.PROJECT],
	suggestionsFilter: () => true,
};

type GlobalSearchModalProps = {
	companyId: string | null;
	personId: number | null;
	isEmbedded?: boolean;
	modalYOffset?: number;
	modalXPosition?: number;
	modalYPosition?: number;
	modalWidth?: number;
	searchModalConfig?: GlobalSearchModalConfig;
	hideSearch: () => void;
	onResultSelected?: (selectedItem) => void;
};
const GlobalSearchModal = ({
	companyId,
	personId,
	isEmbedded = false,
	modalYOffset = 12,
	modalXPosition,
	modalYPosition = 12,
	modalWidth,
	searchModalConfig = defaultSearchModalConfig,
	hideSearch,
	onResultSelected,
}: GlobalSearchModalProps): ReactPortal | null => {
	const recentSearchKey = searchModalConfig.recentSearchKey;
	const recentSearches: string[] = useMemo(() => parseLocalStorageJSONOrDefault(recentSearchKey, []), []);

	// If we have limited search to a specific project, switch to showing task indicator on results instead of project indicator
	const showTaskIndicator = !!searchModalConfig.projectId;

	const [showMore, setShowMore] = useState(false);
	const [searchData, setSearchData] = useState<SearchQueryResponse>({
		searchResults: [],
		accessibleProjectIds: {projectIds: [], projectGroupIds: []},
	});
	const [suggestionsData, setSuggestionsData] = useState<SearchSuggestion[]>([]);
	const [searchString, setSearchString] = useState('');
	const [isLoading, setIsLoading] = useState(false);

	const baseSearchString = searchModalConfig?.baseSearch;

	const searchResults = searchData?.searchResults || [];
	const accessibleProjectIds = searchData?.accessibleProjectIds;

	const searchInputRef = useRef<HTMLInputElement>(null);
	const showMoreRef = useRef<HTMLButtonElement>(null);

	const latestSuccessfulSearchRef = useRef('');
	const searchStringHistoryRef = useRef<string[]>([]);
	const firstInSessionRef = useRef(true);

	const abortControllerRef = useRef(new AbortController());

	// Don't run modal tracking if the modal is embedded.
	useTrackModal(isEmbedded ? null : 'Global Search Modal');

	const size = useWindowSize();
	const windowHeight = size?.height ?? window.innerHeight;

	const handleSearchStringChange = (value: string) => {
		setIsLoading(true);
		setSearchString(value);
	};

	const fetchSearchSuggestions = async () => {
		const recentTaskId = localStorage.getItem(recentVisitedTaskKey);
		const result = await searchSuggestionQuery(recentTaskId);
		setSuggestionsData(result);
	};

	const fetchSearchData = async (query: string) => {
		abortControllerRef.current.abort(); // FIXME: Consider if this should be moved down to the handleSearchInputChange field
		abortControllerRef.current = new AbortController();
		const actualSearch = baseSearchString ? baseSearchString + ' ' + query : query;

		const result = await searchQuery(
			actualSearch,
			searchModalConfig.projectId,
			searchModalConfig.searchEndpoint,
			abortControllerRef.current.signal,
			personId
		);
		if (result) {
			searchStringHistoryRef.current.push(query);
			setIsLoading(false);
			setSearchData(result);
		}
	};

	// Do an initial search if a baseSearch query is defined
	useEffect(() => {
		if (baseSearchString) {
			setIsLoading(true);
			fetchSearchData('');
		}
	}, []);

	const trackSearch = useCallback(
		(query: string) => {
			trackEvent(searchModalConfig.trackingObject, 'Searched', {searchString: query});
		},
		[searchString]
	);

	const fetchSearchDataDebounced = useMemo(() => {
		return debounce(fetchSearchData, 150);
	}, []);

	const trackSearchDebounced = useMemo(() => {
		return debounce(trackSearch, 1000);
	}, []);

	const updateSearchString = useCallback((value: string) => {
		fetchSearchData(value);
		trackSearch(value);
		handleSearchStringChange(value);
	}, []);

	const updateSearchStringDebounced = useCallback(
		(searchString: string) => {
			fetchSearchDataDebounced(searchString);
			trackSearchDebounced(searchString);
			handleSearchStringChange(searchString);
		},
		[fetchSearchDataDebounced, trackSearchDebounced]
	);

	const handleRecentSelected = useCallback(
		(value: string) => {
			trackEvent(`${searchModalConfig.trackingObject} Recent Item`, 'Selected');
			updateSearchString(value);
			searchInputRef.current?.focus();
		},
		[searchInputRef.current]
	);

	const trackAndHideSearch = useCallback(
		(selectedItem = null, hideSearchModal = true) => {
			if (selectedItem && onResultSelected) {
				onResultSelected(selectedItem);
			}
			if (hideSearchModal) {
				hideSearch();
			}

			if (selectedItem || latestSuccessfulSearchRef.current !== searchString) {
				trackGlobalSearch({
					trackingObject: searchModalConfig.trackingObject,
					companyId,
					personId,
					searchString,
					searchStringHistory: searchStringHistoryRef.current,
					selectedItem,
					searchResults,
					accessibleProjectIds,
					firstInSession: firstInSessionRef.current,
				});

				latestSuccessfulSearchRef.current = searchString;
				firstInSessionRef.current = false;
			}
		},
		[companyId, personId, searchString, searchResults]
	);

	const showRecentSearches = !searchString && !baseSearchString;
	const showShowMoreButton = !showRecentSearches && searchResults.length > SEARCH_SHOWN_LIMIT;
	const showTypeSelectors =
		!searchString.includes('type:') &&
		!(searchModalConfig.allowedEntities.length === 1 && searchString.length > 0) &&
		(!baseSearchString || (!!baseSearchString && !baseSearchString.includes('type:')));

	const searchResultsFormatted = useMemo(() => {
		return showMore ? searchResults : searchResults.slice(0, SEARCH_SHOWN_LIMIT);
	}, [showMore, searchResults]);

	const recentSearchesFormatted = useMemo(() => {
		return recentSearches.slice(0, 3).map(recentVal => ({name: recentVal, variant: SearchResultVariant.Recent}));
	}, [recentSearches]);

	const suggestionsFormatted = useMemo(() => {
		return suggestionsData?.filter(searchModalConfig.suggestionsFilter).map(suggestion => ({
			...suggestion,
			variant: SearchResultVariant.Suggestion,
		}));
	}, [suggestionsData, searchModalConfig]);

	const shownSearchResultsCount = showRecentSearches
		? recentSearchesFormatted.length + suggestionsFormatted?.length
		: searchResultsFormatted.length;

	const searchInputIndex = 0;
	const showMoreButtonIndex = shownSearchResultsCount + 1;
	const [focus, setFocus] = useRoveFocus(shownSearchResultsCount + (showShowMoreButton ? 2 : 1)); // Show more button (if visible) and search input are both included in rove focus

	const toggleShowMore = () => {
		trackEvent(
			!showMore ? `${searchModalConfig.trackingObject} Show More` : `${searchModalConfig.trackingObject} Show Less`,
			'Clicked'
		);
		setShowMore(!showMore);

		if (showMore) {
			setFocus(SEARCH_SHOWN_LIMIT);
		} else {
			setFocus(showMoreButtonIndex);
		}
	};

	const setType = type => {
		updateSearchString(`type:${type} ${searchString || ''}`);
		if (searchInputIndex === focus) {
			searchInputRef.current?.focus();
		} else {
			setFocus(searchInputIndex);
		}
	};

	const focusSearchInput = useCallback(() => {
		setFocus(searchInputIndex);
		searchInputRef.current?.focus();
	}, [searchInputIndex]);

	const focusFirstResult = useCallback(() => {
		if (shownSearchResultsCount > 0) {
			setFocus(1);
		}
	}, [shownSearchResultsCount]);

	const handleModalKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Escape' && !e.defaultPrevented) {
				e.stopPropagation();
				trackAndHideSearch();
			}
		},
		[companyId, personId, searchString, searchResults]
	);

	useEventListener('keydown', handleModalKeyDown, document);

	useEffect(() => {
		if (focus === searchInputIndex) {
			searchInputRef.current?.focus();
		} else if (focus === showMoreButtonIndex) {
			showMoreRef.current?.focus();
		}
	}, [focus]);

	useEffect(() => {
		return () => {
			fetchSearchDataDebounced.cancel();
			trackSearchDebounced.cancel();
		};
	}, [fetchSearchDataDebounced, trackSearchDebounced]);

	useEffect(() => {
		fetchSearchSuggestions();
		return () => {
			abortControllerRef.current?.abort();
		};
	}, []);

	const portalRoot = document.querySelector('#root-portal-container');
	return portalRoot
		? ReactDOM.createPortal(
				<SearchModalBackdrop>
					<ClickAwayListener onClickAway={() => trackAndHideSearch()} mouseEvent={'onMouseDown'}>
						<SearchModal
							role="search"
							aria-label="Site-wide"
							modalYOffset={modalYOffset}
							modalYPosition={modalYPosition}
							modalXPosition={modalXPosition}
							modalWidth={modalWidth}
						>
							<SearchInputIconWrapper>
								{isLoading ? <MiniLoader /> : <Icon icon={'search'} size={'m'} />}
								<GlobalSearchInput
									ref={searchInputRef}
									searchString={searchString}
									recentSearchKey={recentSearchKey}
									recentSearches={recentSearches}
									inputPlaceholder={searchModalConfig.inputPlaceholder}
									updateSearchString={updateSearchString}
									updateSearchStringDebounced={updateSearchStringDebounced}
									focusSearchInput={focusSearchInput}
									focusFirstResult={focusFirstResult}
								/>
							</SearchInputIconWrapper>
							<SectionDivider />
							{showTypeSelectors && (
								<FlexColumn gap="l">
									<Text color="medium" size="2">
										{searchString.length > 0 ? 'Filter by type' : 'Show all'}
									</Text>
									<FlexRow gap="s" wrap={true}>
										{searchModalConfig.allowedEntities.includes(SEARCH_ENTITIES.TASK) && (
											<Button
												icon="task"
												emphasis="medium"
												onPress={() => setType('task')}
												data-cy={'global-search-task-button'}
											>
												Task
											</Button>
										)}
										{searchModalConfig.allowedEntities.includes(SEARCH_ENTITIES.PROJECT) && (
											<Button
												icon="project"
												emphasis="medium"
												onPress={() => setType('project')}
												data-cy={'global-search-project-button'}
											>
												Project
											</Button>
										)}
										{searchModalConfig.allowedEntities.includes(SEARCH_ENTITIES.INTERNAL_TIME) && (
											<Button
												icon="internalTime"
												emphasis="medium"
												onPress={() => setType('internal_time')}
												data-cy={'global-search-internal-time-button'}
											>
												Internal time
											</Button>
										)}
									</FlexRow>
								</FlexColumn>
							)}
							{shownSearchResultsCount > 0 ? (
								<SearchSection>
									<DeprecatedScrollToNativeScroll
										maxHeight={`${
											windowHeight -
											(getStaticContentSize(searchModalConfig.showGiveFeedback, showTypeSelectors) +
												modalYPosition)
										}px`}
										nativeMaxHeight={
											windowHeight -
											(getStaticContentSize(searchModalConfig.showGiveFeedback, showTypeSelectors) +
												modalYPosition)
										}
										hasFocusableContent
									>
										{!showRecentSearches && searchResults.length > 0 && (
											<FlexColumn gap={'s'}>
												<FlexColumn gap={'xs'}>
													<Text color="medium" size="2">
														{`Results (${
															searchResults.length >= 100 ? '99+' : searchResults.length
														})`}
													</Text>
													<SearchResults
														searchResults={searchResultsFormatted}
														allowLinks={searchModalConfig.allowLinks}
														focus={focus}
														setFocus={setFocus}
														indexOffset={0}
														trackAndHideSearch={trackAndHideSearch}
														handleRecentSelected={handleRecentSelected}
														showTaskIndicator={showTaskIndicator}
													/>
												</FlexColumn>
												{showShowMoreButton && (
													<ShowMore
														ref={showMoreRef}
														onClick={toggleShowMore}
														data-cy={'search-modal-show-more'}
													>
														{showMore ? 'Show Less' : 'Show More'}
													</ShowMore>
												)}
											</FlexColumn>
										)}
										{showRecentSearches && recentSearches.length > 0 && (
											<FlexColumn gap={'xs'}>
												<Text color="medium" size="2">
													Recent
												</Text>
												<SearchResults
													searchResults={recentSearchesFormatted}
													allowLinks={searchModalConfig.allowLinks}
													focus={focus}
													setFocus={setFocus}
													indexOffset={0}
													trackAndHideSearch={trackAndHideSearch}
													handleRecentSelected={handleRecentSelected}
													searchResultSection={SearchResultVariant.Recent}
												/>
											</FlexColumn>
										)}
										{showRecentSearches && suggestionsFormatted && suggestionsFormatted.length > 0 && (
											<>
												{recentSearches.length > 0 ? <SectionDivider /> : null}
												<FlexColumn gap={'xs'}>
													<Text color="medium" size="2">
														Suggestions
													</Text>
													<SearchResults
														searchResults={suggestionsFormatted}
														allowLinks={searchModalConfig.allowLinks}
														focus={focus}
														setFocus={setFocus}
														indexOffset={recentSearchesFormatted.length}
														trackAndHideSearch={trackAndHideSearch}
														handleRecentSelected={handleRecentSelected}
													/>
												</FlexColumn>
											</>
										)}
									</DeprecatedScrollToNativeScroll>
								</SearchSection>
							) : !isLoading && (searchString || baseSearchString) ? (
								<SearchResultEmptyState searchString={searchString} />
							) : null}
						</SearchModal>
					</ClickAwayListener>
				</SearchModalBackdrop>,
				portalRoot
				// eslint-disable-next-line no-mixed-spaces-and-tabs
		  )
		: null;
};

export default GlobalSearchModal;
